import { fade, hex, type Color } from "../../common/color.js";
import { insets, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { BaseTheme, derivePalette, makeMetrics } from "./BaseTheme.js";
import type {
  CaptionButton,
  ItemSlotVisual,
  RadialWedgeVisual,
  StatBarVisual,
  VisualState,
  WindowVisualState,
} from "./Theme.js";

/**
 * "Parchment" — the tabletop RPG character-sheet look: aged paper, stained
 * oak frames, gold rules and a serif face. Every framed surface gets a thin
 * gold inner line, which is what separates "brown" from "illuminated".
 */

const GOLD = hex("#c9a227");
const GOLD_BRIGHT = hex("#e8c766");
const OAK = hex("#6b4423");
const OAK_DARK = hex("#4a2f18");
const PARCHMENT = hex("#f2e5c8");
const INK = hex("#33241a");
const WINE = hex("#8c2f2f");
/** Draw-path colors, parsed once rather than on every call. */
const TAN_EDGE = hex("#a8875c");
const HEADER_PRESSED = hex("#c9b18f");

const SERIF = "'Book Antiqua', 'Palatino Linotype', Palatino, Georgia, serif";

export class RpgTheme extends BaseTheme {
  constructor() {
    super({
      name: "rpg",
      isDark: false,
      metrics: makeMetrics({
        titleBarHeight: 32,
        windowBorder: 3,
        windowCornerRadius: 4,
        resizeGripSize: 7,
        captionButtonWidth: 40,
        controlCornerRadius: 3,
        controlHeight: 30,
        buttonPadding: insets(16, 6),
        buttonMinWidth: 96,
        focusRingWidth: 1,
        focusRingInset: 3,
        textBoxPadding: insets(10, 5),
        checkBoxSize: 18,
        radioSize: 18,
        scrollBarSize: 15,
        listItemHeight: 26,
        headerHeight: 28,
        columnPadding: 9,
        font: { family: SERIF, size: 14 },
        captionFont: { family: SERIF, size: 14, weight: 700 },
        monoFont: { family: "'Courier New', monospace", size: 13 },
      }),
      palette: derivePalette({
        desktop: hex("#241708"),
        surface: PARCHMENT,
        raised: hex("#e4d2ac"),
        field: hex("#fbf4e2"),
        ink: INK,
        accent: WINE,
        accentText: hex("#fdf6e3"),
        border: OAK,
        titleBar: OAK,
        titleText: hex("#f5e2b8"),
        isDark: false,
        overrides: {
          windowBorderActive: OAK_DARK,
          windowBorderInactive: hex("#7d6248"),
          titleBarInactive: hex("#8a6b4a"),
          controlBorder: hex("#a8875c"),
          controlBorderHover: OAK,
          fieldBorder: hex("#a8875c"),
          fieldBorderFocused: GOLD,
          textSubtle: hex("#6d5843"),
          textDisabled: hex("#a2947f"),
          selectionBg: WINE,
          selectionText: hex("#fdf6e3"),
          selectionInactiveBg: hex("#c9b18f"),
          textSelectionBg: fade(WINE, 0.35),
          scrollTrack: hex("#ddc9a2"),
          scrollThumb: OAK,
          scrollThumbHover: hex("#845a33"),
          headerBg: hex("#d9c39a"),
          headerBgHover: hex("#e3d0ac"),
          headerText: hex("#4a3320"),
          gridLine: hex("#c2a97f"),
          focusRing: GOLD,
          shadow: hex("#00000055"),
        },
      }),
    });
  }

  /** A gold hairline just inside the frame, and a rule under the caption. */
  protected override decorateWindow(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const b = this.metrics.windowBorder;
    const gold = s.active ? GOLD : fade(GOLD, 0.45);
    rc.strokeRect(
      { x: b - 1, y: b - 1, width: bounds.width - (b - 1) * 2, height: bounds.height - (b - 1) * 2 },
      gold,
      1,
      this.metrics.windowCornerRadius,
    );
    rc.fillRect(
      { x: b, y: b + this.captionHeight(s) - 2, width: bounds.width - b * 2, height: 2 },
      gold,
    );
  }

  /** Buttons look stamped: a gold inset line inside the wooden edge. */
  protected override decorateButton(rc: RenderContext, r: Rect, s: VisualState): void {
    if (s.disabled) return;
    const gold = s.isDefault === true ? GOLD_BRIGHT : s.hovered ? GOLD : fade(GOLD, 0.55);
    rc.strokeRect(
      { x: r.x + 2, y: r.y + 2, width: r.width - 4, height: r.height - 4 },
      gold,
      1,
      Math.max(0, this.controlRadius - 1),
    );
  }

  protected override captionButtonHighlight(button: CaptionButton, pressed: boolean): Color {
    if (button === "close") return pressed ? hex("#6d2323") : WINE;
    return pressed ? hex("#5a3a1e") : hex("#7d5730");
  }

  protected override closeGlyphInk(): Color {
    return hex("#fdf6e3");
  }

  override drawPanel(rc: RenderContext, r: Rect, sunken: boolean): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: sunken ? p.fieldBg : p.windowBg,
      radius: this.controlRadius,
      borderWidth: 2,
      borderColor: sunken ? TAN_EDGE : OAK,
    });
    rc.strokeRect(
      { x: r.x + 2, y: r.y + 2, width: r.width - 4, height: r.height - 4 },
      fade(GOLD, 0.5),
      1,
      Math.max(0, this.controlRadius - 1),
    );
  }

  override drawListBackground(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: s.disabled ? p.fieldBgDisabled : p.fieldBg,
      radius: this.controlRadius,
      borderWidth: 2,
      borderColor: s.focused ? GOLD : TAN_EDGE,
    });
  }

  // --- game chrome --------------------------------------------------------

  override drawStatBarFrame(rc: RenderContext, r: Rect, s: StatBarVisual): void {
    // Ink ruled on parchment, with the gold inner line every framed surface
    // in this theme carries.
    rc.fillRect(r, fade(INK, 0.12), 2);
    rc.strokeRect(r, s.low && !s.disabled ? WINE : OAK_DARK, 1, 2);
    rc.strokeRect(
      { x: r.x + 1, y: r.y + 1, width: r.width - 2, height: r.height - 2 },
      fade(GOLD, 0.45),
      1,
      1,
    );
  }

  override drawItemSlot(rc: RenderContext, r: Rect, s: ItemSlotVisual): void {
    // A tooled leather square on the character sheet: aged paper inside an
    // oak edge, gilded when something is in it.
    rc.fillRect(r, s.locked ? fade(INK, 0.16) : fade(PARCHMENT, 0.55), 2);
    rc.strokeRect(r, s.highlighted ? GOLD_BRIGHT : s.rarity ?? OAK, 1, 2);
    if (s.filled || s.hovered) {
      rc.strokeRect(
        { x: r.x + 2, y: r.y + 2, width: r.width - 4, height: r.height - 4 },
        fade(GOLD, s.hovered ? 0.8 : 0.4),
        1,
        1,
      );
    }
  }

  override drawDialogueBox(rc: RenderContext, r: Rect): void {
    // An unrolled page: parchment, an oak edge, and a gold rule inside it.
    rc.fillRect(r, fade(PARCHMENT, 0.97), 3);
    rc.strokeRect(r, OAK_DARK, 2, 3);
    rc.strokeRect(
      { x: r.x + 4, y: r.y + 4, width: r.width - 8, height: r.height - 8 },
      fade(GOLD, 0.6),
      1,
      2,
    );
  }

  override drawRadialWedge(rc: RenderContext, w: RadialWedgeVisual): void {
    const fill = w.disabled
      ? fade(INK, 0.16)
      : w.highlighted
        ? fade(GOLD_BRIGHT, 0.9)
        : fade(w.color ?? PARCHMENT, 0.85);
    rc.fillArc(w.center, w.innerRadius, w.outerRadius, w.startAngle, w.endAngle, fill);
    // The gold rule every framed surface in this theme carries, bent round.
    rc.fillArc(
      w.center,
      w.outerRadius - 1.5,
      w.outerRadius,
      w.startAngle,
      w.endAngle,
      w.disabled ? OAK_DARK : GOLD,
    );
  }

  override drawResourceBackground(rc: RenderContext, r: Rect): void {
    rc.fillRect(r, fade(PARCHMENT, 0.6), 2);
    rc.strokeRect(r, fade(GOLD, 0.5), 1, 2);
  }

  override drawColumnHeader(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.fillRect(r, s.pressed ? HEADER_PRESSED : s.hovered ? p.headerBgHover : p.headerBg);
    rc.hLine(r.x, r.y + r.height - 1, r.width, GOLD);
    rc.vLine(r.x + r.width - 1, r.y + 4, r.height - 8, TAN_EDGE);
  }
}
